import { apiContract } from '@lore/contracts';
import { initClient } from '@ts-rest/core';

import type { LoreClient } from '../../cli/src/lib/api.ts';
import { copyFileSnapshot, removeSnapshot, watchedFileStateForPath } from '../../cli/src/upload/files.ts';
import {
  resolveClaudeSessionPath,
  resolveExportEntry,
} from '../../cli/src/upload/harnesses/claude.ts';
import type { LocalThreadFile } from '../../cli/src/upload/harnesses/types.ts';
import { repoOriginPathForProject } from '../../cli/src/upload/repo.ts';
import { waitForThreadParsing } from '../../cli/src/upload/uploadSession.ts';
import { uploadThreadFiles } from '../../cli/src/upload/uploadThreadFiles.ts';

export type UploadHarness = 'claude_code' | 'codex' | 'cursor';

export type UploadClientOptions = {
  /**
   * Hostname (or full origin) of the Lore API. Defaults to `api.lore.link`.
   * A bare hostname is requested over https; pass a full origin such as
   * `http://127.0.0.1:4000` to target a local or test server.
   */
  apiHostname?: string;
};

export type UploadSessionSnapshotOptions = {
  /**
   * Caller-owned metadata attached to the uploaded session (max 20 keys,
   * values up to 512 characters). Lets the key owner filter threads by their
   * own identifiers, e.g. `{ customer_id: 'cus_123' }`.
   */
  metadata?: Record<string, string>;
};

export type UploadSessionSnapshotResult = {
  threadId: string;
  /** True when the server already had this exact snapshot and reused the thread. */
  reused: boolean;
};

const DEFAULT_API_HOSTNAME = 'api.lore.link';
const SDK_VERSION = process.env.LORE_SDK_VERSION ?? process.env.npm_package_version ?? 'dev';

export function resolveApiOrigin(apiHostname: string | undefined): string {
  const raw = (apiHostname ?? DEFAULT_API_HOSTNAME).trim() || DEFAULT_API_HOSTNAME;
  const origin = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(raw) ? raw : `https://${raw}`;
  return origin.replace(/\/+$/, '');
}

export class Upload {
  readonly #client: LoreClient;

  constructor(uploadApiKey: string, options: UploadClientOptions = {}) {
    const key = uploadApiKey?.trim();
    if (!key) {
      throw new Error('Lore.Upload requires an Upload API key (lore_uak_...)');
    }
    this.#client = initClient(apiContract, {
      baseUrl: resolveApiOrigin(options.apiHostname),
      baseHeaders: {
        'User-Agent': `@loredotlink/sdk/${SDK_VERSION}`,
        authorization: `Bearer ${key}`,
      },
    }) as unknown as LoreClient;
  }

  /**
   * Locate the local session transcript for `sessionID`, upload a snapshot of
   * it to Lore, and wait for parsing to finish. Resolves with the resulting
   * thread id. The snapshot passes through the same client-side scrub gate as
   * the Lore CLI; a snapshot the scrubber refuses to release is quarantined
   * locally and reported as an error here (nothing is uploaded).
   */
  async uploadSessionSnapshot(
    harness: UploadHarness,
    sessionID: string,
    options: UploadSessionSnapshotOptions = {},
  ): Promise<UploadSessionSnapshotResult> {
    if (harness !== 'claude_code') {
      throw new Error(`harness "${harness}" is not implemented yet — only "claude_code" is supported`);
    }
    const sessionId = sessionID?.trim();
    if (!sessionId) {
      throw new Error('uploadSessionSnapshot requires a session id');
    }

    const localFile = await collectClaudeSessionFile(sessionId);
    try {
      const [outcome] = await uploadThreadFiles(this.#client, [localFile], {
        uploadMetadata: options.metadata,
      });
      if (!outcome) {
        throw new Error('upload session response missing thread file');
      }
      if (outcome.status === 'quarantined') {
        throw new Error(
          `session ${sessionId} was held by the local scrubber (${outcome.reason}, ${outcome.findingCount} finding(s)) and was not uploaded`,
        );
      }
      if (outcome.status === 'no_upload') {
        const detail = outcome.error?.trim() ? `: ${outcome.error.trim()}` : '';
        throw new Error(`upload session for ${sessionId} returned no upload URL and no reusable thread${detail}`);
      }

      const threadId = outcome.thread?.id?.trim();
      if (!threadId) {
        throw new Error(`upload session response missing thread id for session ${sessionId}`);
      }

      await waitForThreadParsing(this.#client, threadId);
      return { threadId, reused: outcome.status === 'deduped' };
    } finally {
      await removeSnapshot(localFile.tempPath);
    }
  }
}

// Mirrors the CLI's `lore export` local-file collection: resolve the history
// entry (falling back to an on-disk scan of ~/.claude/projects), snapshot the
// transcript, and describe it as a LocalThreadFile for the upload chokepoint.
async function collectClaudeSessionFile(sessionId: string): Promise<LocalThreadFile> {
  const entry = await resolveExportEntry({ sessionId, project: process.cwd() });
  const sessionPath = await resolveClaudeSessionPath(entry);
  const watchedState = await watchedFileStateForPath(sessionPath.fullPath);
  if (!watchedState.exists) {
    throw new Error(`session transcript not found at ${sessionPath.fullPath}`);
  }

  const repoOriginPath = await repoOriginPathForProject(entry.project);
  const snapshot = await copyFileSnapshot(sessionPath.fullPath);

  return {
    key: sessionPath.key,
    filename: sessionPath.relativeFilename,
    sourcePath: sessionPath.fullPath,
    repoOriginPath,
    tempPath: snapshot.tempPath,
    md5: snapshot.md5,
    fileType: 'transcript',
    harness: 'claudeCode',
    harnessInternalId: entry.sessionId?.trim() ?? '',
    watchedState,
  };
}
